import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';

import type { IRobloxGameRankRow } from '../types';
import {
  normalizeRobloxRowRecord,
  unwrapRobloxFieldValue,
} from './robloxRankRowValue';

const getRankValue = (rank: unknown): number => {
  const rankValue = Number(unwrapRobloxFieldValue(rank));
  return Number.isFinite(rankValue) ? rankValue : Number.POSITIVE_INFINITY;
};

const getGameLabel = (row: IRobloxGameRankRow): string => {
  return String(row?.entity_name || row?.game_id || '');
};

export const filterRobloxDownloadRows = (
  rows: IRobloxGameRankRow[],
  params: IRecord,
): IRobloxGameRankRow[] => {
  const dataMethod =
    params?.[DOWNLOAD_CFG_KEY.DATA_METHOD] ?? params?.data_method;
  const selectTop =
    Number(params?.[DOWNLOAD_CFG_KEY.SELECT_TOP] ?? params?.data_amount) || 0;
  const downloadType = params?.select_top_type || 'time';
  const gameIds = Array.isArray(params?.game_ids) ? params.game_ids.map(String) : [];

  let filteredRows = rows;

  if (dataMethod === 'checkedLines' && gameIds.length) {
    filteredRows = filteredRows.filter((row) => gameIds.includes(String(row?.game_id || '')));
  }

  if (params?.api_top_applied) {
    return filteredRows;
  }

  if (dataMethod !== 'selectTop' || selectTop <= 0) {
    return filteredRows;
  }

  if (downloadType === 'game') {
    const groupedMap = new Map<string, IRobloxGameRankRow[]>();

    filteredRows.forEach((row) => {
      const key = `${row?.source || ''}__${row?.game_id || ''}`;
      if (!groupedMap.has(key)) {
        groupedMap.set(key, []);
      }
      groupedMap.get(key)?.push(row);
    });

    const selectedGameKeys = [...groupedMap.entries()]
      .sort((left, right) => {
        const leftBestRank = Math.min(...left[1].map((item) => getRankValue(item?.rank)));
        const rightBestRank = Math.min(...right[1].map((item) => getRankValue(item?.rank)));
        if (leftBestRank !== rightBestRank) {
          return leftBestRank - rightBestRank;
        }

        return getGameLabel(left[1][0]).localeCompare(getGameLabel(right[1][0]));
      })
      .slice(0, selectTop)
      .map(([key]) => key);

    const selectedGameKeySet = new Set(selectedGameKeys);
    return filteredRows.filter((row) => {
      const key = `${row?.source || ''}__${row?.game_id || ''}`;
      return selectedGameKeySet.has(key);
    });
  }

  const groupedBySourceDate = new Map<string, IRobloxGameRankRow[]>();
  filteredRows.forEach((row) => {
    const key = `${row?.source || ''}__${row?.date || ''}`;
    if (!groupedBySourceDate.has(key)) {
      groupedBySourceDate.set(key, []);
    }
    groupedBySourceDate.get(key)?.push(row);
  });

  const selectedRowKeys = new Set<string>();
  groupedBySourceDate.forEach((groupRows) => {
    groupRows
      .slice()
      .sort((left, right) => getRankValue(left?.rank) - getRankValue(right?.rank))
      .slice(0, selectTop)
      .forEach((row) => {
        selectedRowKeys.add(`${row?.source || ''}__${row?.date || ''}__${row?.game_id || ''}`);
      });
  });

  return filteredRows.filter((row) => {
    const key = `${row?.source || ''}__${row?.date || ''}__${row?.game_id || ''}`;
    return selectedRowKeys.has(key);
  });
};

export const sortRobloxDownloadRows = (
  rows: IRobloxGameRankRow[],
  params: IRecord,
): IRobloxGameRankRow[] => {
  const sourceOrder = Array.isArray(params?.sources_order) ? params.sources_order : [];
  const downloadType = params?.select_top_type || 'time';

  return [...rows].sort((left, right) => {
    const leftSourceIndex = sourceOrder.indexOf(String(left?.source || ''));
    const rightSourceIndex = sourceOrder.indexOf(String(right?.source || ''));
    const normalizedLeftSourceIndex = leftSourceIndex === -1 ? Number.MAX_SAFE_INTEGER : leftSourceIndex;
    const normalizedRightSourceIndex = rightSourceIndex === -1 ? Number.MAX_SAFE_INTEGER : rightSourceIndex;

    if (normalizedLeftSourceIndex !== normalizedRightSourceIndex) {
      return normalizedLeftSourceIndex - normalizedRightSourceIndex;
    }

    if (downloadType === 'game') {
      const gameDiff = getGameLabel(left).localeCompare(getGameLabel(right));
      if (gameDiff !== 0) {
        return gameDiff;
      }

      return String(left?.date || '').localeCompare(String(right?.date || ''));
    }

    const dateDiff = String(left?.date || '').localeCompare(String(right?.date || ''));
    if (dateDiff !== 0) {
      return dateDiff;
    }

    return getRankValue(left?.rank) - getRankValue(right?.rank);
  });
};

export const appendRobloxDownloadRankChange = (
  rows: IRobloxGameRankRow[],
): Array<IRobloxGameRankRow & { rank_change_value?: number | string }> => {
  const timelineMap = new Map<string, Array<{ date: string; rank: number }>>();

  rows.forEach((row) => {
    const key = `${row?.source || ''}__${row?.game_id || ''}`;
    const currentRank = getRankValue(row?.rank);
    if (!Number.isFinite(currentRank)) {
      return;
    }

    if (!timelineMap.has(key)) {
      timelineMap.set(key, []);
    }

    timelineMap.get(key)?.push({
      date: String(row?.date || ''),
      rank: currentRank,
    });
  });

  const changeMap = new Map<string, number | string>();
  timelineMap.forEach((timeline, key) => {
    const sortedTimeline = timeline
      .slice()
      .sort((left, right) => left.date.localeCompare(right.date));

    sortedTimeline.forEach((item, index) => {
      if (index === 0) {
        changeMap.set(`${key}__${item.date}`, '-');
        return;
      }

      const previousRank = sortedTimeline[index - 1]?.rank;
      changeMap.set(`${key}__${item.date}`, previousRank - item.rank);
    });
  });

  return rows.map((row) => {
    const timelineKey = `${row?.source || ''}__${row?.game_id || ''}__${row?.date || ''}`;
    return {
      ...row,
      rank_change_value: changeMap.get(timelineKey) ?? '-',
    };
  });
};

export const normalizeRobloxDownloadRows = (
  rawList: IRobloxGameRankRow[],
  entityInfoMap: Record<string, Record<string, any>> = {},
): IRobloxGameRankRow[] => {
  return rawList.map((item) => {
    const normalizedItem = normalizeRobloxRowRecord(item);
    const gameId = String(normalizedItem?.game_id || '');
    const entityInfo = gameId
      ? normalizeRobloxRowRecord(entityInfoMap[gameId] || {})
      : undefined;

    const mergedRow: IRobloxGameRankRow = {
      ...normalizedItem,
      entity_info: entityInfo,
    };

    if (entityInfo) {
      Object.entries(entityInfo).forEach(([key, value]) => {
        const currentValue = mergedRow[key];
        if (currentValue === undefined || currentValue === null || currentValue === '') {
          mergedRow[key] = value;
        }
      });
    }

    return mergedRow;
  });
};
